// /admin/subscriptions — venue subscriptions, with Cancel (in Stripe when
// Stripe bills it: now, optionally refunding the last payment, or at the end
// of the paid period) and Withdraw for a scheduled cancellation.
// API: /api/admin/subscriptions (+ /:id/cancel, /:id/resume).
import { Badge, Button, Checkbox, Group, Radio, Select, Stack, Table, Text, Textarea, Title } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { AdminActionModal } from '../../components/admin-shell/AdminActionModal';
import { AdminShell } from '../../components/admin-shell/AdminShell';
import { AdminTableSection } from '../../components/admin-shell/AdminTableSection';
import { showToast } from '../../lib/toast';
import { adminPost } from './_api';
import { usePaginatedQuery } from './_hooks';

interface SubRow {
  id: string;
  venue_account_id: string;
  venue_name: string | null;
  stripe_billed: boolean;
  tier: string;
  state: string;
  billing_cycle: string;
  price_amount_minor: number;
  price_currency: string;
  trial_ends_at: string | null;
  next_charge_at: string | null;
  cancelled_at: string | null;
  cancel_at: string | null;
  created_at: string;
}

type CancelResult = {
  ok: true;
  cancel_at: string | null;
  refund: { id: string; amount_minor: number; currency: string } | null;
};

export const Route = createFileRoute('/admin/subscriptions')({
  component: AdminSubscriptionsPage,
});

function AdminSubscriptionsPage() {
  const qc = useQueryClient();
  const [state, setState] = useState<string | null>(null);
  const [tier, setTier] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<SubRow | null>(null);
  const [when, setWhen] = useState<'period_end' | 'now'>('period_end');
  const [refund, setRefund] = useState(false);
  const [reason, setReason] = useState('');

  const { data, isLoading, error, page, setPage, totalPages, bindFilter } = usePaginatedQuery<SubRow>({
    queryKey: ['admin', 'subscriptions'],
    endpoint: '/api/admin/subscriptions',
    filters: { state, tier },
  });

  const cancel = useMutation({
    mutationFn: (row: SubRow) =>
      adminPost<CancelResult>(`/api/admin/subscriptions/${row.id}/cancel`, {
        when,
        refund_last_payment: when === 'now' && refund,
        reason,
      }),
    onSuccess: async (res, row) => {
      const name = row.venue_name ?? 'The subscription';
      const parts = [
        res.cancel_at !== null ? `${name} ends on ${formatDate(res.cancel_at)}.` : `${name} is cancelled.`,
        res.refund !== null ? `Refunded ${money(res.refund.amount_minor, res.refund.currency)}.` : null,
      ];
      showToast({ kind: 'success', title: 'Cancelled', body: parts.filter(Boolean).join(' ') });
      setCancelling(null);
      await qc.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
    },
  });

  const resume = useMutation({
    mutationFn: (row: SubRow) => adminPost(`/api/admin/subscriptions/${row.id}/resume`, {}),
    onSuccess: async (_r, row) => {
      showToast({ kind: 'success', title: 'Kept', body: `${row.venue_name ?? 'The subscription'} will keep renewing.` });
      await qc.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
    },
    onError: (err: Error) => showToast({ kind: 'error', title: 'Could not withdraw', body: err.message }),
  });

  function openCancel(row: SubRow) {
    cancel.reset();
    setWhen(row.stripe_billed ? 'period_end' : 'now');
    setRefund(false);
    setReason('');
    setCancelling(row);
  }

  return (
    <AdminShell>
      <Stack gap="md">
        <Title order={2}>Subscriptions</Title>

        <Group gap="sm" wrap="wrap">
          <Select
            placeholder="State"
            data={['trial', 'active', 'past_due', 'cancelled', 'paused']}
            clearable
            value={state}
            onChange={bindFilter(setState)}
            style={{ minWidth: 160 }}
          />
          <Select
            placeholder="Tier"
            data={['founding', 'standard', 'premium']}
            clearable
            value={tier}
            onChange={bindFilter(setTier)}
            style={{ minWidth: 160 }}
          />
        </Group>

        <AdminTableSection<SubRow>
          data={data}
          isLoading={isLoading}
          error={error}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          emptyMessage="No subscriptions."
          header={
            <Table.Tr>
              <Table.Th>Venue</Table.Th>
              <Table.Th>Tier</Table.Th>
              <Table.Th>State</Table.Th>
              <Table.Th>Price</Table.Th>
              <Table.Th>Trial ends</Table.Th>
              <Table.Th>Next charge</Table.Th>
              <Table.Th />
            </Table.Tr>
          }
          renderRow={(s) => (
            <Table.Tr key={s.id}>
              <Table.Td>
                <Text size="sm" fw={500}>
                  {s.venue_name ?? `${s.venue_account_id.slice(0, 8)}…`}
                </Text>
                <Text size="xs" c="dimmed">
                  {s.stripe_billed ? 'Billed by Stripe' : 'Not billed by Stripe'}
                </Text>
              </Table.Td>
              <Table.Td>
                <Badge variant="light">{s.tier}</Badge>
              </Table.Td>
              <Table.Td>
                <Badge color={stateColor(s.state)} variant="light">
                  {s.state}
                </Badge>
                {s.cancel_at !== null && s.state !== 'cancelled' && (
                  <Text size="xs" c="orange">
                    Ends {formatDate(s.cancel_at)}
                  </Text>
                )}
              </Table.Td>
              <Table.Td>
                {money(s.price_amount_minor, s.price_currency)} / {s.billing_cycle === 'annual' ? 'year' : 'month'}
              </Table.Td>
              <Table.Td>{s.trial_ends_at ? formatDate(s.trial_ends_at) : '–'}</Table.Td>
              <Table.Td>{s.state !== 'cancelled' && s.cancel_at === null && s.next_charge_at ? formatDate(s.next_charge_at) : '–'}</Table.Td>
              <Table.Td align="right">
                {s.state === 'cancelled' ? null : s.cancel_at !== null ? (
                  <Button
                    size="xs"
                    variant="subtle"
                    loading={resume.isPending && resume.variables?.id === s.id}
                    onClick={() => resume.mutate(s)}
                  >
                    Keep subscription
                  </Button>
                ) : (
                  <Button size="xs" variant="subtle" color="red" onClick={() => openCancel(s)}>
                    Cancel…
                  </Button>
                )}
              </Table.Td>
            </Table.Tr>
          )}
        />
      </Stack>

      <AdminActionModal
        opened={cancelling !== null}
        onClose={() => setCancelling(null)}
        title={`Cancel ${cancelling?.venue_name ?? 'subscription'}`}
        onConfirm={() => cancelling !== null && cancel.mutate(cancelling)}
        confirmLabel={when === 'now' ? 'Cancel now' : 'Cancel at period end'}
        cancelLabel="Back"
        confirmColor="red"
        confirmDisabled={reason.trim().length < 5}
        loading={cancel.isPending}
        error={cancel.error?.message ?? null}
      >
        {cancelling?.stripe_billed ? (
          <Radio.Group value={when} onChange={(v) => setWhen(v as typeof when)} label="When">
            <Stack gap="xs" mt="xs">
              <Radio
                value="period_end"
                label={`At the end of the paid period${cancelling.next_charge_at ? ` (${formatDate(cancelling.next_charge_at)})` : ''}`}
                description="No further charges; the venue stays listed until then."
              />
              <Radio value="now" label="Now" description="Ends straight away and pauses their venues." />
            </Stack>
          </Radio.Group>
        ) : (
          <Text size="sm">Stripe doesn&apos;t bill this subscription, so it ends now.</Text>
        )}
        {cancelling?.stripe_billed && when === 'now' && (
          <Checkbox
            checked={refund}
            onChange={(e) => setRefund(e.currentTarget.checked)}
            label="Refund their last payment"
            description="Through Stripe. Any referral payout on it is cancelled automatically."
          />
        )}
        <Textarea
          label="Reason"
          description="Kept in the audit log."
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          minRows={2}
          autosize
        />
      </AdminActionModal>
    </AdminShell>
  );
}

function stateColor(state: string): string {
  if (state === 'active') return 'tide';
  if (state === 'trial') return 'blue';
  if (state === 'past_due') return 'yellow';
  if (state === 'cancelled') return 'gray';
  return 'gray';
}

function money(minor: number, currency: string): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(minor / 100);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Australia/Brisbane',
  });
}
